import { Plugin, TextSelection } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import markExtents from './markExtents';
import makeValidator from './makeValidator';

// const utils = App.utils;
// ? We could make links link to themselves by default.
const makeViewHandler = (eventType, eventHandlerFromUpdate, elementFromUpdate) => {
  let handler = null;
  let element = null;
  return view => {
    // Remove the old handler on the old element.
    if (handler) element.removeEventListener(eventType, handler);
    // Make a final call to clear.
    if (view) {
      handler = eventHandlerFromUpdate(view);
      element = elementFromUpdate(view);
      element.addEventListener(eventType, handler);
    }
  };
};

const protocolWhitelist = ['http', 'https'];
const protocolWhitelistRegex = new RegExp(`(${protocolWhitelist.join('|')}):`, 'i');
// TODO: URL isn't the most tolerant parser, we should use something better.
const linkIsValid = value => {
  if (typeof value !== 'string') return `URL (${value}) is not a supported type.`;
  try {
    const url = new window.URL(value);
    if (!protocolWhitelistRegex.test(url.protocol))
      return `URL (${value}) contains unsupported protocol.`;
  } catch (e) {
    return `URL (${value}) is not parsable.`;
  }
  return true;
};

const hasProtocolRegex = /^[^:/]+:\/\//;
const hasDomainRegex = /^([^:/]+:\/\/)?[^/]+\.[^/]+/;
const LinkValidator = makeValidator(
  linkIsValid,
  { normalize: value => (hasProtocolRegex.test(value) ? value : 'http://' + value) },
  ''
);

function markActive(state, type) {
  let { from, $from, to, empty } = state.selection;
  if (empty) return type.isInSet(state.storedMarks || $from.marks());
  else return state.doc.rangeHasMark(from, to, type);
}

const LinkEditorPlugin = () => {
  // Create the link editor container.
  const linkEditorContainerEl = document.createElement('div');
  linkEditorContainerEl.classList.add('link-editor');
  linkEditorContainerEl.classList.add('link-editor--closed');

  // Create the LinkEditor interface.
  const linkURLEditorInputEl = document.createElement('input');
  linkURLEditorInputEl.type = 'text';
  linkURLEditorInputEl.placeholder = 'Link Address';
  linkURLEditorInputEl.classList.add('link-editor__input');

  const linkClearButtonEl = document.createElement('a');
  linkClearButtonEl.href = '#';
  linkClearButtonEl.text = 'Clear';
  linkClearButtonEl.classList.add('link-editor__clear');

  const linkSaveButtonEl = document.createElement('a');
  linkSaveButtonEl.href = '#';
  linkSaveButtonEl.text = 'Set';
  linkSaveButtonEl.classList.add('link-editor__save');

  const linkEditButtonEl = document.createElement('a');
  linkEditButtonEl.href = '#';
  linkEditButtonEl.text = 'Edit';
  linkEditButtonEl.classList.add('link-editor__edit');

  const linkGotoEl = document.createElement('a');
  linkGotoEl.href = '#';
  linkGotoEl.text = '^';
  linkGotoEl.target = '_blank';
  linkGotoEl.classList.add('link-editor__link');

  linkEditorContainerEl.appendChild(linkGotoEl);
  linkEditorContainerEl.appendChild(linkURLEditorInputEl);
  linkEditorContainerEl.appendChild(linkClearButtonEl);
  linkEditorContainerEl.appendChild(linkSaveButtonEl);
  linkEditorContainerEl.appendChild(linkEditButtonEl);

  const plugin = new Plugin({
    view: view => {
      view.dom.parentElement.appendChild(linkEditorContainerEl);
      let lastSelection = null;
      let validator = null;
      const handleLinkURLEditorInput = makeViewHandler(
        'input',
        view => event => {
          validator.update(event.target.value);
        },
        view => plugin.getState(view.state).linkURLEditorInputEl
      );
      const handleClearLinkButton = makeViewHandler(
        'click',
        view => event => {
          event.preventDefault();
          const { selection } = plugin.getState(view.state);
          const transaction = view.state.tr;
          transaction.removeMark(selection.from, selection.to, selection.mark);
          view.dispatch(transaction);
        },
        view => plugin.getState(view.state).linkClearButtonEl
      );
      const handleEditLinkButton = makeViewHandler(
        'click',
        view => event => {
          event.preventDefault();
          linkEditorContainerEl.classList.add('link-editor--editing');
          linkEditorContainerEl.classList.remove('link-editor--viewing');
        },
        view => plugin.getState(view.state).linkEditButtonEl
      );
      const handleSaveLinkButton = makeViewHandler(
        'click',
        view => event => {
          event.preventDefault();

          if (validator.error) {
            console.error(validator.errorMessage);
            return;
          }

          const { selection, linkGotoEl } = plugin.getState(view.state);

          linkGotoEl.text = validator.normalValue;
          linkGotoEl.href = validator.normalValue;
          linkEditorContainerEl.classList.remove('link-editor--editing');
          linkEditorContainerEl.classList.add('link-editor--viewing');

          const transaction = view.state.tr;
          transaction.removeMark(selection.from, selection.to, selection.mark);
          transaction.addMark(
            selection.from,
            selection.to,
            view.state.schema.marks.link.create({ href: validator.normalValue })
          );
          view.dispatch(transaction);
        },
        view => plugin.getState(view.state).linkSaveButtonEl
      );
      const handleApplyLinkOnEnter = makeViewHandler(
        'keypress',
        view => event => {
          // Key Code "Enter"
          if (event.keyCode === 13) {
            event.preventDefault();

            if (validator.error) {
              return;
            }

            const { selection } = plugin.getState(view.state);

            const transaction = view.state.tr;
            transaction.removeMark(selection.from, selection.to, selection.mark);
            transaction.addMark(
              selection.from,
              selection.to,
              view.state.schema.marks.link.create({ href: validator.normalValue })
            );

            // Move the selection to the end of the link, closing the link editor.
            transaction.setSelection(
              TextSelection.create(transaction.doc, selection.to, selection.to)
            );

            view.dispatch(transaction);
          }
        },
        view => plugin.getState(view.state).linkURLEditorInputEl
      );

      return {
        update(view, state) {
          // Hook things that are changed, here.
          const pluginState = plugin.getState(view.state);
          if (pluginState.selection && pluginState.selection !== lastSelection) {
            lastSelection = pluginState.selection;

            const href = pluginState.selection.mark.attrs.href;
            pluginState.linkURLEditorInputEl.value = href;
            pluginState.linkGotoEl.text = href;
            pluginState.linkGotoEl.href = href;
            linkEditorContainerEl.classList.add('link-editor--opened');
            linkEditorContainerEl.classList.remove('link-editor--closed');
            if (href) {
              linkEditorContainerEl.classList.remove('link-editor--editing');
              linkEditorContainerEl.classList.add('link-editor--viewing');
            } else {
              linkEditorContainerEl.classList.add('link-editor--editing');
              linkEditorContainerEl.classList.remove('link-editor--viewing');
            }
            validator = new LinkValidator({
              value: href,
              // onChange: console.log,
              onError: console.log
            });
            handleLinkURLEditorInput(view);
            handleClearLinkButton(view);
            handleEditLinkButton(view);
            handleSaveLinkButton(view);
            handleApplyLinkOnEnter(view);
          } else {
            linkEditorContainerEl.classList.remove('link-editor--open');
            linkEditorContainerEl.classList.add('link-editor--closed');
          }
        }
      };
    },
    state: {
      init({ schema, plugins }, editorState) {
        const selection = markExtents(editorState, schema.marks.link);
        return {
          linkEditorContainerEl,
          linkURLEditorInputEl,
          linkClearButtonEl,
          linkGotoEl,
          linkSaveButtonEl,
          linkEditButtonEl,
          selection
        };
      },
      apply(transaction, pluginState, previousEditorState, editorState) {
        pluginState = { ...pluginState};
        // Update the selection when the plugin is active and something has changed.
        pluginState.selection = markExtents(editorState, editorState.schema.marks.link);

        // Update the plugin selection state when we don't have a selection, or we select outside of the current selection.
        if (
          !pluginState.selection ||
          editorState.selection.from < pluginState.selection.from ||
          editorState.selection.to > pluginState.selection.to
        ) {
          pluginState.selection = null;
        }

        if (transaction.getMeta('focus-link')) {
          // Focus the input as soon as the transaction completes.
          // TODO: Make sure this only happens forward in history.
          setTimeout(() => {
            pluginState.linkURLEditorInputEl.focus();
          }, 0);
        }

        return pluginState;
      }
    },
    props: {
      decorations(state) {
        const pluginState = this.getState(state);
        if (!pluginState.selection) return null;
        return DecorationSet.create(state.doc, [
          Decoration.inline(pluginState.selection.from, pluginState.selection.to, {
            class: 'ProseMirror-editor__illuminate'
          })
        ]);
      }
    },
    appendTransaction(transactions, previousEditorState, editorState) {
      const pluginState = plugin.getState(editorState);
      const previousPluginState = plugin.getState(previousEditorState);
      // Bum Links are Removed
      if (
        // The selection has changed.
        pluginState.selection !== previousPluginState.selection &&
        // And, there was a selection.
        previousPluginState.selection instanceof Object &&
        // And, the current selection has a different mark or isn't in a mark.
        (pluginState.selection === null ||
          pluginState.selection.mark !== previousPluginState.selection.mark) &&
        // And, the last selection had an invalid mark.
        // TODO: We could check the link a little better here and/or add some error decorations.
        !previousPluginState.selection.mark.attrs.href 
      ) {
        // Delete the mark, wherever it is.
        return editorState.tr.removeMark(
          0,
          editorState.doc.content.size,
          previousPluginState.selection.mark
        );
      }
    }
  });
  return plugin;
};

export default LinkEditorPlugin;
